import { Button, Card, Divider, Flex, Modal } from 'antd';
import { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// components
import ComponentHeader from 'components/componentHeader/componentHeader';
import StatusComponent from 'components/status/status';
import BackButton from 'components/backButton/backButton';
import AnnouncementDetailsComponent from '../components/announcementContent';
import PageTitle from 'components/title/title';
import { useNavigate, useParams } from 'react-router-dom';
import { Announcement } from '../models/announcement';
import { onCancelAnnouncement, onGetAnnouncementById, onPublishAnnouncement } from '../services/anouncementServices';
import NotificationService from 'core/services/notification';
import LoadingComponent from 'components/loadingComponent/loadingComponent';
import { StatusColors } from 'utils/statusColors';
import { ExclamationCircleFilled } from '@ant-design/icons';

// services

// styles
import smsIcon from 'assets/icons/sms.webp';
import VerificationModal from '../../verificationModal';
import { VerificationPayload } from '../../commonModels';

const { confirm } = Modal;

// models

interface AnnouncementViewPageProps {}

const AnnouncementViewPage: FunctionComponent<AnnouncementViewPageProps> = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { id } = useParams<{ id: string }>();

	const [announcement, setAnnouncement] = useState<Announcement>();
	const [englishAvailable, setEnglishAvailable] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState<boolean>(true);

	const [isPublishing, setIsPublishing] = useState<boolean>(false);
	const [isCancelling, setIsCancelling] = useState<boolean>(false);
	const [verificationModalOpen, setVerificationModalOpen] = useState<boolean>(false);

	useEffect(() => {
		getAnnouncementDetails();
	}, []);

	const getAnnouncementDetails = async () => {
		try {
			setIsLoading(true);

			const response = await onGetAnnouncementById(id as string);

			setAnnouncement(response.announcement);
			setEnglishAvailable(response.available_in_english);

			setIsLoading(false);
		} catch (error) {
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
			navigate('/dashboard/actions/announcement');
		}
	};

	const onCancelPublish = async () => {
		const modal = confirm({
			icon: <ExclamationCircleFilled style={{ color: 'red' }} />,
			title: t('ACTIONS.CANCEL_ANNOUNCEMENT_PUBLISH_CONFIRMATION'),
			content: t('ACTIONS.CANT_REPUBLISH_AGAIN'),
			okText: t('COMMON.CONFIRM'),
			okType: 'primary',
			okButtonProps: { loading: isCancelling },
			cancelText: t('COMMON.CANCEL'),
			cancelButtonProps: { type: 'link', danger: true },
			async onOk() {
				try {
					setIsCancelling(true);
					modal.update({ okButtonProps: { loading: true } });

					await onCancelAnnouncement(id as string);

					setIsCancelling(false);

					navigate('/dashboard/actions/announcement');
					NotificationService.success(t('COMMON.SUCCESS'), t('ACTIONS.ANNOUNCEMENT_CANCELLED_SUCCESSFULLY'));
				} catch (error) {
					setIsCancelling(false);
					NotificationService.error(t('COMMON.ERROR'), t('COMMON.SUBMIT_ERROR'));
				}
			},
			onCancel() {
			},
		});
	};

	const onPublish = async ({ key, method }: VerificationPayload) => {
		try {
			setIsPublishing(true);

			await onPublishAnnouncement(id as string, {
				verified_using: method,
				[method]: key,
			});

			NotificationService.success(t('COMMON.SUCCESS'), t('ACTIONS.ANNOUNCEMENT_PUBLISHED_SUCCESSFULLY'));
			setVerificationModalOpen(false);
			navigate('/dashboard/actions/announcement');
			setIsPublishing(false);
		} catch (error: any) {
			setIsPublishing(false);

			const errorMessage = error?.response?.data?.data?.errors || [error?.response?.data?.message];
			const messages = Object.values(errorMessage || {}).flat() as string[];

			const errors = (
				<ul>
					{messages.map((msg, index) => (
						<li key={index}>{msg}</li>
					))}
				</ul>
			);

			NotificationService.error(t('COMMON.ERROR'), messages.length ? errors : t('COMMON.SUBMIT_ERROR'));
		}
	};

	return (
		<>
			<PageTitle name={t('TITLE.ACTIONS')} />

			<BackButton />

			{isLoading ? (
				<LoadingComponent />
			) : (
				<Card>
					<div>
						<Flex align="center" gap={10}>
							<h5 className="mb-0">#{announcement?.request_number}</h5>
							<StatusComponent
								color={StatusColors[announcement?.status as string]}
								text={
									announcement?.status === 'Approved'
										? `${t(`COMMON.${announcement?.status.toUpperCase()}`)} ${t('ACTIONS.WAITING_FOR_PUBLISH')} `
										: t(`COMMON.${announcement?.status.toUpperCase()}`)
								}
							/>
						</Flex>
						<p className="text-light">{announcement?.created_at}</p>
					</div>

					<div>
						<h5 style={{ fontSize: '1.1rem' }}>{t('ACTIONS.AUDIENCE')}</h5>
						<div className="mt-4">
							<h6 className="fw-medium mb-0">{t('ACTIONS.PUBLIC')}</h6>
							<p className="text-light">{t('ACTIONS.PUBLISH_TO_PUBLIC')}</p>
						</div>

						{announcement?.notified_by_sms && (
							<Flex gap={15} align="center" className="containerDiv p-2 w-max">
								<img src={smsIcon} style={{ width: '40', height: 40 }} />
								<div>
									<p className="fw-medium mb-0">{t('ACTIONS.SMS_ENABLED')}</p>
									<StatusComponent
										text={t('ACTIONS.WHEN_YOU_PUBLISH')}
										color="green"
										small
										iconName="energy"
										iconHeight={15}
										iconWidth={10}
									/>
								</div>
							</Flex>
						)}
					</div>

					<Divider />

					<ComponentHeader title={t('ACTIONS.ANNOUNCEMENT_DETAILS')} />

					<AnnouncementDetailsComponent
						language="ar"
						title={announcement?.title.ar!}
						excerpt={announcement?.excerpt.ar!}
						content={announcement?.content.ar!}
					/>

					{englishAvailable && (
						<div className="mt-3">
							<Divider />
							<AnnouncementDetailsComponent
								language="en"
								title={announcement?.title.en!}
								excerpt={announcement?.excerpt.en!}
								content={announcement?.content.en!}
							/>
						</div>
					)}

					{announcement?.status === 'Approved' && (
						<>
							<Flex justify="flex-end" className="mt-4" gap={10}>
								<Button type="link" danger onClick={onCancelPublish}>
									{t('COMMON.CANCEL')}
								</Button>
								<Button type="primary" className="px-4" onClick={() => setVerificationModalOpen(true)}>
									{t('ACTIONS.PUBLISH')}
								</Button>
							</Flex>

							<VerificationModal
								open={verificationModalOpen}
								isLoading={isPublishing}
								onCancel={() => setVerificationModalOpen(false)}
								onVerificationSuccess={onPublish}
								module="announcement"
							/>
						</>
					)}
				</Card>
			)}
		</>
	);
};

export default AnnouncementViewPage;
